/**
 * utils/tombstone.ts
 * Type guard for tombstone (soft-deleted) records
 *
 * Separated from type definitions per CODING_STANDARDS (src/types/ is type-only).
 *
 * @see SyncDeletable interface in @/types/sync/SyncDeletable
 */

import type { SyncDeletable } from '@/types/sync/SyncDeletable';

/**
 * Type guard to check if a record is tombstoned (soft-deleted).
 *
 * Accepts unknown input for tolerance of any shape. Simply checks
 * for a truthy `deletedAt` field.
 *
 * @param record - Any value to check
 * @returns True if the record has a truthy deletedAt field
 *
 * @example
 * const draft = await db.wizard_drafts.get(key);
 * if (isTombstoned(draft)) {
 *   // Treat as "no draft" - return defaults
 *   return createDefaultDraft();
 * }
 */
export function isTombstoned(
  record: unknown
): record is SyncDeletable & { deletedAt: string } {
  if (!record || typeof record !== 'object') {
    return false;
  }
  const obj = record as Record<string, unknown>;
  return typeof obj['deletedAt'] === 'string' && obj['deletedAt'].length > 0;
}
